import { useCallback, useState } from "react";

import type { EmergencyAddress } from "@dialstack/sdk-webrtc";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen as setSnackbarOpen
} from "components/action-snackbar/action-snackbar-slice";
import {
  setOpen as setConfirmDialogOpen,
  setContent,
  setOnChange,
  setOptions,
  setTitle
} from "components/confirm-dialog/confirm-dialog.slice";

import { formatAddress } from "./e911-address.util";
import { useDialstackSoftphone } from "./use-dialstack-softphone.hook";

export type E911AddressDelete = {
  // Ids removed during this session. `savedAddresses` is only refetched when the
  // socket reconnects, so the deleted row would otherwise sit in the picker
  // until the next reconnect.
  deletedIds: string[];
  requestDelete: (address: EmergencyAddress) => void;
};

/**
 * Removing a redundant saved 911 address, behind the app's shared confirm
 * dialog.
 *
 * This deletes a record a PSAP may be handed, so it is deliberately narrow: the
 * banner only offers it on a row the dedup engine collapsed as a duplicate, and
 * a row that is currently registered to a network is refused outright — another
 * device may be bound to it right now.
 */
export default function useE911AddressDelete(): E911AddressDelete {
  const dispatch = useAppDispatch();
  const { deleteEmergencyAddress } = useDialstackSoftphone();
  const [deletedIds, setDeletedIds] = useState<string[]>([]);

  const notify = useCallback(
    (message: string) => {
      dispatch(setCustomMessage(message));
      dispatch(setSnackbarOpen(true));
    },
    [dispatch]
  );

  const runDelete = useCallback(
    async (address: EmergencyAddress) => {
      try {
        await deleteEmergencyAddress(address.id);
      } catch {
        notify("Could not remove that address. Please try again.");
        return;
      }
      setDeletedIds((current) => [...current, address.id]);
      notify("Address removed");
    },
    [deleteEmergencyAddress, notify]
  );

  const requestDelete = useCallback(
    (address: EmergencyAddress) => {
      // The banner hides the control in this case; this is the guard on the
      // side that actually destroys the record.
      if (address.registered_ip !== null) {
        notify(
          "That address is in use on a network right now, so it can't be removed."
        );
        return;
      }
      dispatch(setTitle("Remove this 911 location?"));
      dispatch(
        setContent(
          `${formatAddress(
            address.address
          )} will be deleted permanently. Your other saved location for this place is kept.`
        )
      );
      dispatch(
        setOptions({ actionButtonType: "danger", actionButtonText: "Remove" })
      );
      dispatch(setOnChange(() => runDelete(address)));
      dispatch(setConfirmDialogOpen(true));
    },
    [dispatch, notify, runDelete]
  );

  return { deletedIds, requestDelete };
}
